import * as React from 'react';

const prefix = 'cdf-nav-group:';
const changeEvent = 'cdf-nav-group-change';
const memoryFallback = new Map<string, boolean>();

function storageKey(scopeKey: string, groupId: string): string {
  return prefix + JSON.stringify([scopeKey, groupId]);
}

function readStored(scopeKey: string, groupId: string): boolean | undefined {
  const key = storageKey(scopeKey, groupId);
  const pendingValue = memoryFallback.get(key);
  if (pendingValue !== undefined) return pendingValue;
  try {
    const value = localStorage.getItem(key);
    if (value === 'true') return true;
    if (value === 'false') return false;
  } catch {
    // Continue with in-memory state when storage is unavailable.
  }
  return undefined;
}

function writeStored(scopeKey: string, groupId: string, open: boolean): void {
  const key = storageKey(scopeKey, groupId);
  try {
    localStorage.setItem(key, String(open));
    memoryFallback.delete(key);
  } catch {
    // A failed write must supersede any older value still readable in storage.
    memoryFallback.set(key, open);
  }
  window.dispatchEvent(new Event(changeEvent));
}

/** Per-person, tenant, entity and heading preference shared by both sidebar panels. */
export function useNavGroupState(scopeKey: string | null) {
  const [, refresh] = React.useReducer((count: number) => count + 1, 0);
  const [temporary, setTemporary] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    const sync = () => refresh();
    window.addEventListener(changeEvent, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(changeEvent, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const isOpen = React.useCallback((groupId: string, defaultCollapsed: boolean) =>
    scopeKey ? readStored(scopeKey, groupId) ?? !defaultCollapsed : temporary[groupId] ?? !defaultCollapsed, [scopeKey, temporary]);

  const setOpen = React.useCallback((groupId: string, open: boolean) => {
    if (scopeKey) writeStored(scopeKey, groupId, open);
    else setTemporary((current) => ({ ...current, [groupId]: open }));
  }, [scopeKey]);

  return { isOpen, setOpen };
}
